import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, CalendarDays } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';

export default function LoginPage() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login, api } = useAuth();
  const navigate = useNavigate();

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
      toast.success(data.message);
    } catch {
      // mostrar el mensaje genérico igualmente para no revelar nada
      setForgotSent(true);
    }
    setForgotLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`${t('welcome')}, ${user.name}`);
      const dest = user.role === 'admin' ? '/admin' : user.role === 'monitor' ? '/monitor' : '/family';
      navigate(dest);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join(' ') : t('invalid_credentials');
      toast.error(msg);
    }
    setSubmitting(false);
  };

  return (
    <>
    <div
      className="min-h-screen w-full flex flex-col items-center overflow-y-auto"
      style={{
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
      data-testid="login-page"
    >
      {/* Velo berenjena translúcido */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ backgroundColor: 'rgba(72,41,58,0.42)', zIndex: 0 }}
      />

      {/* Selector de idioma arriba a la derecha */}
      <div className="relative w-full flex justify-end px-6 pt-5" style={{ zIndex: 10 }}>
        <LanguageSelector />
      </div>

      {/* Logo protagonista — grande y centrado */}
      <div className="relative flex flex-col items-center px-6 pt-4 pb-6" style={{ zIndex: 10 }}>
        <img
          src="/logo.png"
          alt="El Club de Manri"
          style={{ width: 'min(85vw, 480px)', height: 'auto' }}
          className="drop-shadow-2xl"
        />
      </div>

      {/* Botón actividades — prominente */}
      <div className="relative pb-5 pt-1 text-center" style={{ zIndex: 10 }}>
        <button
          onClick={() => navigate('/actividades')}
          className="btn-shimmer inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-base font-bold transition-all hover:scale-105 active:scale-95"
          style={{
            background: 'rgba(251,248,243,0.92)',
            color: '#48293A',
            border: '2px solid rgba(251,248,243,0.95)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.30)',
          }}
        >
          <CalendarDays size={22} />
          Consulta aquí las actividades y horarios
        </button>
      </div>

      {/* Tarjeta formulario */}
      <div className="relative w-full max-w-md px-4 pb-14" style={{ zIndex: 10 }}>
        <div
          className="rounded-3xl shadow-2xl p-8"
          style={{ backgroundColor: 'rgba(251,248,243,0.97)' }}
        >
          {!forgotMode ? (
            <>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#48293A] mb-1">
                {t('login_title')}
              </h1>
              <p className="text-[#4C4D57] text-sm mb-6">
                {t('login_subtitle')}
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">
                    {t('email_label')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="familia@email.com"
                    className="input-manrique"
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#48293A] mb-1.5">
                    {t('password_label')}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-manrique"
                    required
                    data-testid="login-password-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-manrique w-full flex items-center justify-center gap-2 mt-2"
                  data-testid="login-submit-button"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>{t('login_button')} <ArrowRight size={18} /></>
                  )}
                </button>
              </form>
              <div className="mt-4 text-center">
                <button
                  onClick={() => { setForgotMode(true); setForgotEmail(email); }}
                  className="text-sm text-[#48293A]/70 hover:text-[#48293A] hover:underline"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => { setForgotMode(false); setForgotSent(false); }}
                className="flex items-center gap-1 text-sm text-[#48293A]/70 hover:text-[#48293A] mb-4"
                data-testid="forgot-back-button"
              >
                <ArrowLeft size={15} /> Volver al login
              </button>
              <h1 className="font-heading font-bold text-xl tracking-tight text-[#48293A] mb-1">
                Recuperar contraseña
              </h1>
              {!forgotSent ? (
                <>
                  <p className="text-[#4C4D57] text-sm mb-5">
                    Introduce tu email y te enviaremos un enlace para crear una nueva contraseña.
                  </p>
                  <form onSubmit={handleForgot} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#48293A] mb-1.5">
                        {t('email_label')}
                      </label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="familia@email.com"
                        className="input-manrique"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="btn-manrique w-full flex items-center justify-center gap-2"
                      data-testid="forgot-submit-button"
                    >
                      {forgotLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>Enviar enlace <ArrowRight size={18} /></>
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <div className="mt-2 p-5 bg-[#27AE60]/8 border border-[#27AE60]/20 rounded-2xl text-center space-y-2" data-testid="forgot-sent-message">
                  <p className="text-[#27AE60] font-semibold text-sm">Correo enviado</p>
                  <p className="text-[#4C4D57] text-sm">
                    Si el email está registrado, recibirás un enlace de recuperación. Revisa también la carpeta de spam.
                  </p>
                  <button
                    onClick={() => { setForgotMode(false); setForgotSent(false); }}
                    className="text-sm text-[#48293A] hover:underline font-medium mt-2"
                  >
                    Volver al login
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>

</>
  );
}
